import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgePercent, CalendarDays, Check, CheckCircle2, ChevronDown, Info, Loader2, MessageCircle, Send, Sparkles } from "lucide-react";
import { api, apiError, cachedGet } from "@/lib/api";
import { toast } from "sonner";
import { PACKAGES } from "@/components/Packages";
import Reveal from "@/components/Reveal";
import { waLink } from "@/App";

const inputCls = "w-full bg-ink border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

const STEPS = [
  { icon: CalendarDays, title: "Pick your date", text: "Tell us your occasion and tentative date — we confirm availability within a day." },
  { icon: BadgePercent, title: "Pay 50% advance", text: "A simple online payment locks your date. The balance is due on the day of the event." },
  { icon: Sparkles, title: "We handle the rest", text: "Lineup, setlist and the flow of the evening — you simply soak in the satsang with your guests." },
];

const FALLBACK_TESTIMONIALS = [
  { text: "It felt like a temple and a concert at once. Three thousand people singing the final aarti together — I had goosebumps for an hour.", name: "Meera K.", context: "Bhajan Sandhya, Mumbai" },
  { text: "We booked Sumiran for our parents' 50th anniversary. The elders wept, the youngsters recorded everything. The most meaningful evening of our family's year.", name: "Rohit & Anjali S.", context: "Private Show, Udaipur" },
  { text: "Production quality of a stadium act, soul of a satsang. Our corporate retreat has never seen anything like the closing kirtan.", name: "Vikram D.", context: "Corporate Evening, Gurugram" },
];

export default function BookPrivateShow() {
  const [allPackages, setAllPackages] = useState(PACKAGES);
  const [settings, setSettings] = useState(null);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", city: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/packages").then(({ data }) => { if (data.length) setAllPackages(data); }).catch(() => {});
    cachedGet("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    cachedGet("/testimonials").then(({ data }) => { if (data.length) setTestimonials(data); }).catch(() => {});
    cachedGet("/faqs", 30000).then(({ data }) => setFaqs(data)).catch(() => {});
  }, []);

  // The single offer: the admin-marked master package, falling back to the
  // lowest-priced package so the page never renders empty.
  const byPrice = [...allPackages].sort((a, b) => a.price - b.price);
  const master = byPrice.find((p) => p.master) || byPrice[0] || null;
  const videoEmbed = settings?.private_show_video_embed || "";
  const videoPortrait = settings?.private_show_video_orientation === "portrait";

  const scrollToOffer = () => {
    document.querySelector('[data-testid="bps-offer"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, event_type: "Private Show", package: master.name });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(apiError(err, "Could not send your booking request"));
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="bookprivateshow-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <Link to="/" className="flex items-baseline gap-2" data-testid="bps-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/book" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="bps-book-link">Concert Tickets</Link>
          <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="bps-back-home">Back to site</Link>
        </div>
      </header>

      {done ? (
        <main className="px-6 md:px-12 py-14 md:py-20 max-w-6xl mx-auto">
          <div className="text-center py-16 max-w-xl mx-auto" data-testid="bps-success">
            <CheckCircle2 size={48} className="mx-auto text-saffron" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-4xl text-bone">Request received.</h1>
            <p className="mt-4 text-bone/70 leading-relaxed">
              Thank you, {form.name}. Your request for <span className="text-gold">{master.name}</span> is
              with the band — expect a call within a day to plan your evening.
            </p>
            <Link to="/" className="mt-10 inline-block px-10 py-3.5 border border-bone/25 text-bone hover:border-saffron hover:text-saffron transition-colors" data-testid="bps-success-home">
              Back to site
            </Link>
          </div>
        </main>
      ) : (
        <main>
          {/* ---------- Hero ---------- */}
          <section className="relative px-6 md:px-12 pt-16 md:pt-24 pb-16 md:pb-20 overflow-hidden" data-testid="bps-hero">
            <div className="ember-glow w-[420px] h-[420px] -top-40 -left-40 opacity-20" aria-hidden="true" />
            <div className={`relative max-w-6xl mx-auto grid grid-cols-1 ${videoEmbed ? "lg:grid-cols-2" : ""} gap-12 items-center`}>
              <Reveal>
                <div className={videoEmbed ? "" : "max-w-3xl"}>
                  <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Private Show Booking</p>
                  <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.1]" data-testid="bps-heading">
                    Book the band for <em className="text-gold font-light">your evening</em>
                  </h1>
                  <p className="mt-6 text-base md:text-lg text-ash leading-relaxed max-w-xl">
                    Weddings, anniversaries, house satsangs, corporate evenings — a full devotional
                    concert crafted around your gathering, anywhere in India.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <button onClick={scrollToOffer}
                      className="px-8 py-3.5 bg-saffron text-ink text-sm font-medium tracking-wide hover:bg-gold transition-colors"
                      data-testid="bps-hero-cta">
                      See What's Included
                    </button>
                    <a href={waLink("Hi The Band Sumiran! We'd like to book a private show.")} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-3.5 border border-bone/25 text-bone text-sm hover:border-saffron hover:text-saffron transition-colors"
                      data-testid="bps-hero-whatsapp">
                      <MessageCircle size={15} /> Ask on WhatsApp
                    </a>
                  </div>
                </div>
              </Reveal>
              {videoEmbed && (
                <Reveal delay={0.15}>
                  <div className="border border-bone/15 bg-surface p-2" data-testid="bps-hero-video">
                    <div className={videoPortrait ? "aspect-[9/14] max-h-[540px] mx-auto" : "aspect-video"}>
                      <iframe src={videoEmbed} className="ig-embed-frame" title="The Band Sumiran — private shows"
                        loading="lazy" allowFullScreen scrolling="no" />
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          </section>

          {/* ---------- How it works ---------- */}
          <section className="px-6 md:px-12 py-14 md:py-20 border-t border-bone/10 bg-surface/40" data-testid="bps-how-it-works">
            <div className="max-w-6xl mx-auto">
              <Reveal>
                <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">How it works</p>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl text-bone">Three steps to <em className="text-gold font-light">your evening</em></h2>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.1}>
                    <div className="relative border border-bone/10 bg-ink p-8 h-full" data-testid={`bps-step-${i}`}>
                      <span className="font-serif text-5xl text-saffron/25 select-none" aria-hidden="true">0{i + 1}</span>
                      <span className="absolute top-8 right-8 w-11 h-11 rounded-full border border-saffron/40 flex items-center justify-center">
                        <s.icon size={18} className="text-saffron" />
                      </span>
                      <h3 className="mt-5 font-serif text-2xl text-bone">{s.title}</h3>
                      <p className="mt-2.5 text-sm text-ash leading-relaxed">{s.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- What's Included (single offer) + details form ---------- */}
          {master && (
            <section className="px-6 md:px-12 py-14 md:py-20 max-w-6xl mx-auto">
              <Reveal>
                <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">What's included</p>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl text-bone">One evening, <em className="text-gold font-light">everything taken care of</em></h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 border border-saffron/50 bg-surface p-8 md:p-12 grid grid-cols-1 lg:grid-cols-5 gap-10" data-testid="bps-offer">
                  <div className="lg:col-span-2">
                    <p className="font-serif text-3xl md:text-4xl text-bone leading-tight" data-testid="bps-offer-name">{master.name}</p>
                    <p className="mt-3 font-serif text-5xl text-gold" data-testid="bps-offer-price">
                      ₹{master.price.toLocaleString("en-IN")}<span className="text-sm text-ash font-sans"> /evening</span>
                    </p>
                    <p className="mt-4 text-sm text-ash leading-relaxed">
                      The complete Sumiran private-show experience — one package, no tiers to compare.
                      Same pricing across India.
                    </p>
                    <a href={waLink(`Hi The Band Sumiran! I'd like a quote for the ${master.name} package.`)}
                      target="_blank" rel="noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-xs text-[#25D366] border border-[#25D366]/50 px-3 py-1.5 hover:bg-[#25D366] hover:text-ink transition-colors"
                      data-testid="bps-offer-quote">
                      <MessageCircle size={12} /> Get Quote on WhatsApp
                    </a>
                  </div>
                  <ul className="lg:col-span-3 space-y-3 self-center">
                    <li className="flex items-start gap-3 text-sm text-bone/80" data-testid="bps-offer-inclusion-0">
                      <Check size={15} className="text-saffron mt-0.5 shrink-0" /> 2.5 to 3 hours live performance
                    </li>
                    {master.lineup.map((l, i) => (
                      <li key={l} className="flex items-start gap-3 text-sm text-bone/80" data-testid={`bps-offer-inclusion-${i + 1}`}>
                        <Check size={15} className="text-saffron mt-0.5 shrink-0" /> {l}
                      </li>
                    ))}
                    <li className="flex items-start gap-3 text-sm text-saffron/90">
                      <Info size={15} className="text-saffron mt-0.5 shrink-0" /> Sound setup must be provided by the client
                    </li>
                  </ul>
                </div>
              </Reveal>

              <p className="mt-12 font-display text-xs tracking-[0.3em] text-saffron uppercase">Your details</p>
              <form onSubmit={submit} className="mt-4 max-w-2xl border border-bone/15 bg-surface/60 p-7 md:p-9 space-y-4" data-testid="bps-form">
                <div className="flex items-center justify-between gap-4 border-b border-bone/10 pb-4">
                  <p className="text-sm text-bone">{master.name}</p>
                  <p className="font-serif text-2xl text-gold shrink-0" data-testid="bps-selected-price">₹{master.price.toLocaleString("en-IN")}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="bps-name-input" />
                  <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="bps-email-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required placeholder="Phone (WhatsApp preferred)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="bps-phone-input" />
                  <input required placeholder="Event city" value={form.city} onChange={set("city")} className={inputCls} data-testid="bps-city-input" />
                </div>
                <div>
                  <label className="block text-xs font-display tracking-[0.2em] uppercase text-ash mb-1.5">Tentative event date</label>
                  <input type="date" value={form.event_date} onChange={set("event_date")} className={`${inputCls} [color-scheme:dark]`} data-testid="bps-date-input" />
                </div>
                <textarea rows={3} placeholder="Occasion, guests, venue…" value={form.message} onChange={set("message")} className={inputCls} data-testid="bps-message-input" />
                <p className="text-xs text-ash">50% advance confirms the booking — payable online after the band calls you back.</p>
                <button type="submit" disabled={sending}
                  className="w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="bps-submit-btn">
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                  {sending ? "Sending…" : "Request Booking"}
                </button>
              </form>
            </section>
          )}

          {/* ---------- Testimonials ---------- */}
          <section className="px-6 md:px-12 py-14 md:py-20 border-t border-bone/10 bg-surface/40" data-testid="bps-testimonials">
            <div className="max-w-6xl mx-auto">
              <Reveal>
                <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Voices from private evenings</p>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl text-bone">What hosts <em className="text-gold font-light">tell us</em></h2>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
                {testimonials.slice(0, 3).map((q, i) => (
                  <Reveal key={q.id || q.name} delay={i * 0.1} y={30}>
                    <blockquote className="bg-ink p-8 lg:p-10 h-full flex flex-col" data-testid={`bps-testimonial-${i}`}>
                      <span className="font-serif text-5xl text-saffron/40 leading-none select-none" aria-hidden="true">&ldquo;</span>
                      <p className="mt-4 font-serif italic text-lg leading-relaxed text-bone/85 flex-1">{q.text}</p>
                      <footer className="mt-6">
                        <p className="font-display text-sm tracking-[0.2em] text-gold">{q.name}</p>
                        {q.context && <p className="text-xs text-ash mt-1">{q.context}</p>}
                      </footer>
                    </blockquote>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- FAQs ---------- */}
          {faqs.length > 0 && (
            <section className="px-6 md:px-12 py-14 md:py-20 border-t border-bone/10" data-testid="bps-faqs">
              <div className="max-w-3xl mx-auto">
                <Reveal>
                  <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Good to know</p>
                  <h2 className="mt-3 font-serif text-3xl md:text-4xl text-bone">Questions, <em className="text-gold font-light">answered</em></h2>
                </Reveal>
                <Reveal delay={0.1}>
                  <div className="mt-8 border-t border-bone/10">
                    {faqs.map((f, i) => {
                      const open = openFaq === i;
                      return (
                        <div key={f.id} className="border-b border-bone/10" data-testid={`bps-faq-${i}`}>
                          <button type="button" onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open}
                            className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                            data-testid={`bps-faq-toggle-${i}`}>
                            <span className={`font-serif text-lg md:text-xl transition-colors ${open ? "text-saffron" : "text-bone group-hover:text-gold"}`}>{f.question}</span>
                            <ChevronDown size={16} className={`shrink-0 text-saffron/70 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                          </button>
                          <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                            <div className="overflow-hidden">
                              <p className="pb-5 pr-6 text-sm text-bone/70 leading-relaxed" data-testid={`bps-faq-answer-${i}`}>{f.answer}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Reveal>
              </div>
            </section>
          )}
        </main>
      )}
    </div>
  );
}
